<template>
    <div
        class="sign-ways" 
        @focusin="onFocusIn"
        @focusout="onFocusOut"
    >
        <div class="sign-ways__container">
            <div class="sign-ways__title"> {{ title }} </div>

            <div class="sign-ways__main">
                <div class="sign-ways__code_link">
                    <div class="sign-ways__row">
                        <h5 class="sign-ways__label"><span>1.</span> {{ stepOneTitle }}:</h5>

                        <h5 class="sign-ways__active">{{ url }}</h5>
                    </div>

                    <div class="sign-ways__row">
                        <h5 class="sign-ways__label">
                            <span>2.</span> {{ stepTwoCaption }}
                        </h5>

                        <div class="sign-ways__number">{{ code }}</div>
                    </div>

                    <button ref="newCode" class="sign-ways__btn" @click="fetchLink">
                        {{ buttonCaption }}
                    </button>
                </div>

                <div class="sign-ways__delimiter">
                    <span>{{ delimiterСaption }}</span>
                </div>

                <div class="sign-ways__qr">
                    <h5> {{ qrСaption }} </h5>
                    <qrcode-vue v-if="linkQrCode" class="qr-code-medium sign-ways__qr-code" :value="linkQrCode" :level="'H'" render-as="svg"/>
                </div>
            </div>

            <div class="sign-btn__container">
                <div>{{ loginFootnote }}</div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Strings } from "@/locales/types/enums/strings";
    import { type VerificationCode } from "@ca.vmedia.tv/domains";
    import { useApplicationStore } from "@/stores/Application";
    import { useChannelsStore } from "@/stores/Channels";
    import { linkItems } from "@/components/Header/types/LinkItemsCollection";
    import { useI18n } from "vue-i18n";
    import QrcodeVue from 'qrcode.vue';
    import { useCleanUrl } from "@/composables/useCleanUrl";
    import { useLanguagesUtils } from "@/composables/useLanguagesUtils";
    import { useRouter } from "vue-router";
    import { type Ref, ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
    import { useAccountStore } from "@/stores/Account";
    import { storeToRefs } from 'pinia'
    import { useUserSettingsStore } from "@/stores/UserSettings";
    import { useDeviceSettingsStore } from "@/stores/DeviceSettings";
    import { useClientMessagesStore } from "@/stores/ClientMessages";
    import { useGenresStore } from "@/stores/Genres";
    import { useGuideStore } from "@/stores/Guide";

    const ApplicationStore = useApplicationStore();
    const {
        getCurrentTime: currentTime,
        getIsModalSignInUnavailableShown,
        getIsModalExpiredCodeShown
    } = storeToRefs(ApplicationStore);
    const {
        openModalExpiredCode,
        openModalSignInUnavailable
    } = ApplicationStore;

    const { clearAllGenres } = useGenresStore();

    const { getChannels } = useChannelsStore();
    
    const ClientMessagesStore = useClientMessagesStore();
    const {
        startReceiving
    } = ClientMessagesStore;

    const AccountStoreModule = useAccountStore();
    const {
        FetchVerificationCode,
        SaveUserInfo,
    } = AccountStoreModule;
    const {
        getVerificationCode: verificationCode
    } = storeToRefs(AccountStoreModule);

    const {
        fetchSettings,
    } = useUserSettingsStore();
    const { setupSettings } = useDeviceSettingsStore();

    const {
        resetGuideFilter
    } = useGuideStore();


    const { mainPage } = useAppConfig();
    const { t } = useI18n();
    const router = useRouter();
    const { $navigator } = useNuxtApp();
    const { adjustLanguageSettings } = useLanguagesUtils();
    const loginFootnote: string = t(Strings.LOGIN_OKTA_FREEDOM_FOOTNOTE).toString();
    const qrСaption: string = t(Strings.LOGIN_FORGOT_SCAN).toString();
    const delimiterСaption: string = t(Strings.LOGIN_FORGOT_OR).toString();
    const buttonCaption: string = t(Strings.LOGIN_SIGN_IN_GET_NEW_CODE).toString();
    const stepTwoCaption: string = t(Strings.LOGIN_SIGN_IN_ENTER_CODE).toString();
    const stepOneTitle: string = t(Strings.LOGIN_VISIT).toString();
    const title: string = t(Strings.LOGIN_SIGN_IN_TITILE).toString();
    const link: Ref<string> = ref("");
    const socket: Ref<WebSocket | null> = ref(null);
    const timerUpdateSocket: Ref<ReturnType<typeof setInterval> | null> = ref(null);
    const isRegKeyListeners: Ref<boolean> = ref(false);
    const newCode: Ref<HTMLButtonElement | null> = ref(null);

    const deviceId = computed((): string => {
        return verificationCode.value?.deviceId ?? "";
    });
    const code = computed((): string => {
        return verificationCode.value?.verificationCode ?? "";
    });
    const url = computed((): string => {
        const value = verificationCode.value && verificationCode.value.activationUrl && verificationCode.value.activationUrl.length > 0 ? verificationCode.value.activationUrl : link.value;
        return useCleanUrl(value);
    });
    const linkQrCode = computed((): string => {
        return !!verificationCode.value?.activationUrl && !!code.value ? `${verificationCode.value?.activationUrl}?code=${code.value}` : url.value;
    });
    const activeDate = computed((): number => {
        if (verificationCode.value && verificationCode.value.activeTillDate) {
            const dateString = verificationCode.value.activeTillDate;
            const date = parseInt(dateString.match(/\d+/)![0], 10);
            return date;
        }
        return 0;
    });
    const onFocusIn = (): void => {
        registerKeyListeners();
    };
    const onFocusOut = (): void => {
        unregisterKeyListeners();
    };
    const registerKeyListeners = (): void => {
        if (!isRegKeyListeners.value) {
            $navigator.on("keypress:back", keyBackListener);
            isRegKeyListeners.value = true;
        }
    };
    const unregisterKeyListeners = (): void => {
        if (isRegKeyListeners.value) {
            $navigator.off("keypress:back", keyBackListener);
            isRegKeyListeners.value = false;
        }
    };
    const keyBackListener = (): void => {
        router.go(-1);
    };
    const fetchLink = async (): Promise<void> => {
        closeSocket();
        await FetchVerificationCode();
    };
    const closeSocket = (): void => {
        if (socket.value) {
            socket.value.close();
        }
    };
    const openSocket = (): void => {
        const socketUrl = `wss://a2.vmedia.ca/verificationCode?deviceId=${deviceId.value}`;

        closeSocket();

        socket.value = new WebSocket(socketUrl);

        socket.value.onmessage = async (event: MessageEvent) => {
            const message = event.data;
            closeSocket();
            await SaveUserInfo(message);
            await fetchSettings();
            await getChannels();
            // start receiving api messages - important shouldn't await
            startReceiving().catch((error) => {
                Logger.captureError(error as Error);
            });

            clearAllGenres();

            await setupSettings();
            await adjustLanguageSettings();

            resetGuideFilter();
            await router.push(linkItems[mainPage].routerPath);
        };
    };


    watch(
        verificationCode,
        (val: VerificationCode | null) => {
            if (val && val.verificationCode) {
                openSocket();
                if (timerUpdateSocket.value) {
                    clearInterval(timerUpdateSocket.value);
                }
                timerUpdateSocket.value = setInterval(() => {
                    openSocket();
                }, 90000);
            } else {
                if (timerUpdateSocket.value) {
                    clearInterval(timerUpdateSocket.value);
                }
                closeSocket();
                openModalSignInUnavailable();
            }
        }
    );
    watch(
        currentTime,
        (val: number) => {
            if (val && activeDate.value > 0 && val > activeDate.value) {
                if (timerUpdateSocket.value) {
                    clearInterval(timerUpdateSocket.value);
                }
                closeSocket();
                openModalExpiredCode();
            }
        }
    );
    watch(
        getIsModalExpiredCodeShown,
        (value: boolean) => {
            if (!value) {
                if (newCode.value) {
                    newCode.value.focus();
                }
            }
        }
    );
    watch(
        getIsModalSignInUnavailableShown,
        (value: boolean) => {
            if (!value) {
                if (newCode.value) {
                    newCode.value.focus();
                }
            }
        }
    );


    onMounted(() => {
        fetchLink().catch((error) => {
            Logger.captureError(error as Error);
        });
        if (newCode.value) {
            newCode.value.focus();
        }
    });

    onBeforeUnmount(() => {
        if (timerUpdateSocket.value) {
            clearInterval(timerUpdateSocket.value);
        }
        closeSocket();
        unregisterKeyListeners();
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>